import React, { useRef, useEffect, useState, useCallback } from 'react';

/* Богатый редактор в стиле дизайна «Доработки»:
   тулбар (undo/redo · картинка · блок кода) + всплывающее меню по выделению
   (шрифт · размер · B / I / ссылка / x² / x₂), как мини-панель в Word. Хранит HTML.
   Шрифт и размер пишутся inline-стилем, поэтому у ученика текст такой же.
   Поддержка картинок: вставка из буфера (Ctrl+V), и ресайз перетаскиванием уголка —
   ширина пишется в inline-style (px), поэтому у ученика тот же масштаб.
   props: value, onChange(html), placeholder, onImageUpload(file)=>Promise<url> */

const escapeHtml = (s) => (s || '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
const svg = (inner, sw = 1.9) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">{inner}</svg>
);

// Чистим вставленную разметку: убираем чужие фоны, цвета и шрифты, оставляем
// только структуру и простое форматирование. Иначе текст из Word/сайтов приносит
// background:#fff и фиксированный color — на тёмной теме получаются белые плашки.
const KEEP_STYLE = ['text-align', 'font-weight', 'font-style', 'text-decoration', 'text-decoration-line', 'margin-left', 'margin-right', 'width', 'height', 'max-width', 'display'];

// Шрифты меню выделения — всё, что подключено на платформе, плюс Times New Roman.
// value — что пишем в style; пустое значение = шрифт по умолчанию (Golos Text).
export const EDITOR_FONTS = [
  { label: 'Golos Text', value: '', css: "'Golos Text', sans-serif" },
  // Как прежний заголовок H2 в редакторе: Golos Text толщиной 800 (шрифт — в index.html)
  { label: 'Golos Text ExtraBold', value: "'Golos Text ExtraBold', 'Golos Text', sans-serif" },
  { label: 'Montserrat', value: "'Montserrat', sans-serif" },
  { label: 'Inter', value: "'Inter', sans-serif" },
  { label: 'Manrope', value: "'Manrope', sans-serif" },
  { label: 'Space Grotesk', value: "'Space Grotesk', sans-serif" },
  { label: 'Saar SP Demo', value: "'Saar SP Demo', sans-serif" },
  { label: 'Bebas Neue', value: "'Bebas Neue', sans-serif" },
  { label: 'JetBrains Mono', value: "'JetBrains Mono', monospace" },
  { label: 'Times New Roman', value: "'Times New Roman', Times, serif" },
];
export const EDITOR_SIZES = [10, 11, 12, 13, 14, 15, 16, 18, 20, 22, 24, 28, 32, 36, 48];
const DEFAULT_SIZE = 15;   // базовый размер условия у ученика и в редакторе
const firstFamily = (v) => String(v || '').split(',')[0].trim().replace(/^['"]|['"]$/g, '');
const fontByFamily = (v) => EDITOR_FONTS.find(f => f.label.toLowerCase() === firstFamily(v).toLowerCase());
const IcDD = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>;
const ALLOWED_TAGS = new Set(['P','BR','B','STRONG','I','EM','U','S','STRIKE','SUP','SUB','H1','H2','H3','H4','UL','OL','LI','A','IMG','PRE','CODE','BLOCKQUOTE','DIV','SPAN','TABLE','THEAD','TBODY','TR','TD','TH']);

function cleanPastedHtml(html) {
  const box = document.createElement('div');
  box.innerHTML = html;
  box.querySelectorAll('style, script, meta, link, title').forEach(n => n.remove());

  box.querySelectorAll('*').forEach(el => {
    // <font color=…> и прочие устаревшие обёртки — разворачиваем
    if (el.tagName === 'FONT' || !ALLOWED_TAGS.has(el.tagName)) {
      const parent = el.parentNode;
      if (parent) { while (el.firstChild) parent.insertBefore(el.firstChild, el); parent.removeChild(el); }
      return;
    }
    [...el.attributes].forEach(a => {
      const n = a.name.toLowerCase();
      if (n === 'style' || n === 'src' || n === 'href' || n === 'alt' || n === 'colspan' || n === 'rowspan') return;
      el.removeAttribute(a.name);
    });
    const style = el.getAttribute('style');
    if (style) {
      const kept = style.split(';').map(x => x.trim()).filter(Boolean).filter(decl => {
        const prop = decl.split(':')[0].trim().toLowerCase();
        const val = decl.slice(decl.indexOf(':') + 1).trim();
        // Шрифт и размер оставляем, только если они из меню редактора (копия
        // внутри самого редактора). Calibri 11pt из Word и подобное отбрасываем.
        if (prop === 'font-family') return !!fontByFamily(val)?.value;
        if (prop === 'font-size') { const m = val.match(/^(\d+(?:\.\d+)?)px$/); return !!m && +m[1] >= 8 && +m[1] <= 72; }
        return KEEP_STYLE.includes(prop);
      });
      if (kept.length) el.setAttribute('style', kept.join('; '));
      else el.removeAttribute('style');
    }
    if (el.tagName === 'IMG') el.classList.add('ed-img');
  });

  return box.innerHTML;
}

export default function RichEditor({ value, onChange, placeholder = '', onImageUpload, onAttachFile }) {
  const editorRef = useRef(null);  // внешний .editor (для позиционирования рамки/уголка)
  const areaRef = useRef(null);    // contentEditable
  const inited = useRef(false);
  const selImgRef = useRef(null);  // выбранная картинка
  const [bubble, setBubble] = useState(null); // { left, top, b, i, ff, fs }
  const [menu, setMenu] = useState(null);     // открытый список в меню выделения: 'font' | 'size'
  const [menuUp, setMenuUp] = useState(false); // список открыт вверх — внизу не хватило места
  const [imgSel, setImgSel] = useState(null); // { left, top, w, h } — рамка+уголок над выбранной картинкой

  // начальное содержимое (без управляемого ре-рендера, чтобы не прыгал курсор)
  useEffect(() => {
    if (areaRef.current && !inited.current) {
      areaRef.current.innerHTML = value || '';
      inited.current = true;
    }
  }, [value]);

  const emit = useCallback(() => { onChange?.(areaRef.current?.innerHTML || ''); }, [onChange]);

  // ── Выбор картинки и позиция рамки/уголка ──
  const refreshImgSel = useCallback(() => {
    const img = selImgRef.current;
    const ed = editorRef.current;
    if (!img || !ed || !ed.contains(img)) { setImgSel(null); return; }
    const er = ed.getBoundingClientRect();
    const ir = img.getBoundingClientRect();
    setImgSel({ left: ir.left - er.left, top: ir.top - er.top, w: ir.width, h: ir.height });
  }, []);

  const selectImg = useCallback((img) => { selImgRef.current = img; refreshImgSel(); }, [refreshImgSel]);
  const deselectImg = useCallback(() => { selImgRef.current = null; setImgSel(null); }, []);

  // Любую картинку приводим к классу ed-img (ограничение ширины + единый стиль),
  // даже если она вставилась обычной вставкой (HTML из буфера).
  const normalizeImgs = useCallback(() => {
    const a = areaRef.current; if (!a) return;
    a.querySelectorAll('img:not(.ed-img)').forEach(im => im.classList.add('ed-img'));
  }, []);

  const onAreaClick = (e) => {
    const img = e.target.closest && e.target.closest('img');   // любая картинка
    if (img && areaRef.current?.contains(img)) {
      if (!img.classList.contains('ed-img')) img.classList.add('ed-img');
      // убираем текстовое выделение, чтобы не вылезало bubble-меню поверх уголка
      const s = window.getSelection(); if (s) s.removeAllRanges();
      setBubble(null);
      selectImg(img);
    } else deselectImg();
  };

  // Пересчёт рамки при изменениях/скролле
  useEffect(() => {
    const h = () => refreshImgSel();
    window.addEventListener('resize', h);
    window.addEventListener('scroll', h, true);
    return () => { window.removeEventListener('resize', h); window.removeEventListener('scroll', h, true); };
  }, [refreshImgSel]);

  // ── Ресайз кнопками (надёжно для любых картинок, в т.ч. высоких) ──
  const resizeImg = (mode) => {
    const img = selImgRef.current; const area = areaRef.current; if (!img || !area) return;
    const maxW = area.clientWidth - 36;
    const cur = img.getBoundingClientRect().width;
    let w;
    if (mode === 'fit') w = maxW;
    else if (mode === 'minus') w = Math.round(cur * 0.85);
    else w = Math.round(cur * 1.18); // plus
    w = Math.max(48, Math.min(w, maxW));
    img.style.width = w + 'px'; img.style.height = 'auto';
    img.removeAttribute('width'); img.removeAttribute('height');
    emit(); refreshImgSel();
  };

  // ── Ресайз картинки перетаскиванием уголка ──
  const onHandleDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    const img = selImgRef.current; const area = areaRef.current; if (!img || !area) return;
    const startX = e.clientX;
    const startW = img.getBoundingClientRect().width;
    const maxW = area.clientWidth - 36; // в пределах области (с учётом паддингов)
    const move = (ev) => {
      let w = Math.round(startW + (ev.clientX - startX));
      w = Math.max(48, Math.min(w, maxW));
      img.style.width = w + 'px';
      img.style.height = 'auto';
      img.removeAttribute('width'); img.removeAttribute('height');
      refreshImgSel();
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      emit();
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  // ── Вставка из буфера (Ctrl+V): картинка → загрузка → вставка ──
  const onPaste = async (e) => {
    const items = e.clipboardData && e.clipboardData.items; if (!items) return;
    let imgItem = null;
    for (let i = 0; i < items.length; i++) { if (items[i].type && items[i].type.indexOf('image') === 0) { imgItem = items[i]; break; } }
    if (!imgItem) {
      // Текст/разметка из Word, Google Docs, сайтов тянет за собой чужие inline-стили —
      // в том числе background:#fff и жёстко заданный цвет текста. На тёмной теме это
      // выглядит как белые плашки. Чистим разметку и вставляем сами.
      const html = e.clipboardData.getData('text/html');
      if (!html) return; // простой текст — пусть браузер вставит как есть
      e.preventDefault();
      document.execCommand('insertHTML', false, cleanPastedHtml(html));
      normalizeImgs();
      emit();
      return;
    }
    e.preventDefault();
    let file = imgItem.getAsFile();
    if (!file || !onImageUpload) return;
    // Скриншот/картинка из буфера часто приходит File без нормального имени
    // ("image.png"/пустое) — приводим к корректному имени с расширением по MIME,
    // иначе загрузка на S3 может падать ("ошибка вставки").
    const ext = ((file.type && file.type.split('/')[1]) || 'png').replace('jpeg', 'jpg').split('+')[0];
    file = new File([file], `pasted-${Date.now()}.${ext}`, { type: file.type || 'image/png' });
    // сохраняем позицию курсора (await может её сбросить)
    const sel = window.getSelection();
    const range = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    const url = await onImageUpload(file);
    if (!url) return;
    const a = areaRef.current; if (!a) return;
    a.focus();
    if (range) { const s = window.getSelection(); s.removeAllRanges(); s.addRange(range); }
    document.execCommand('insertHTML', false, `<img class="ed-img" src="${url}" alt=""/>`);
    normalizeImgs();
    emit();
  };

  // ── Выравнивание картинки (лево/центр/право) — пишется в inline-style, у ученика тот же вид ──
  const alignImg = (mode) => {
    const img = selImgRef.current; if (!img) return;
    img.style.display = 'block';
    if (mode === 'center') { img.style.marginLeft = 'auto'; img.style.marginRight = 'auto'; }
    else if (mode === 'right') { img.style.marginLeft = 'auto'; img.style.marginRight = '0'; }
    else { img.style.marginLeft = '0'; img.style.marginRight = 'auto'; } // left
    emit(); refreshImgSel();
  };

  const run = async (cmd) => {
    const a = areaRef.current; if (!a) return; a.focus();
    try {
      switch (cmd) {
        case 'undo': document.execCommand('undo'); break;
        case 'redo': document.execCommand('redo'); break;
        case 'attach': {
          if (onAttachFile) {
            const input = document.createElement('input');
            input.type = 'file';
            input.onchange = () => { const f = input.files && input.files[0]; if (f) onAttachFile(f); };
            input.click();
          }
          break;
        }

        case 'image': {
          if (onImageUpload) {
            const input = document.createElement('input');
            input.type = 'file'; input.accept = 'image/*';
            input.onchange = async () => {
              const f = input.files && input.files[0]; if (!f) return;
              const url = await onImageUpload(f);
              if (url) { a.focus(); document.execCommand('insertHTML', false, `<img class="ed-img" src="${url}" alt=""/>`); emit(); }
            };
            input.click();
          } else {
            const url = window.prompt('Ссылка на изображение:', 'https://');
            if (url) document.execCommand('insertHTML', false, `<img class="ed-img" src="${url}" alt=""/>`);
          }
          break;
        }
        case 'codeblock': {
          const sel = window.getSelection().toString() || 'код';
          document.execCommand('insertHTML', false, `<pre>${escapeHtml(sel)}</pre><p><br></p>`);
          break;
        }
        default: break;
      }
    } catch { /* no-op */ }
    emit();
  };

  // ── Bubble-меню по выделению внутри этого редактора ──
  useEffect(() => {
    const onSel = () => {
      if (selImgRef.current) { setBubble(null); return; }   // выбрана картинка — текстовое меню не показываем
      const sel = window.getSelection();
      const area = areaRef.current;
      if (!sel || sel.isCollapsed || !sel.rangeCount || !area) { setBubble(null); return; }
      const node = sel.anchorNode;
      const inside = node && (area === node || area.contains(node));
      if (!inside) { setBubble(null); return; }
      const r = sel.getRangeAt(0).getBoundingClientRect();
      if (r.width === 0 && r.height === 0) { setBubble(null); return; }
      let b = false, i = false;
      try { b = document.queryCommandState('bold'); i = document.queryCommandState('italic'); } catch { /* */ }
      // Шрифт и размер в начале выделения — показываем на кнопках, как Word
      const range = sel.getRangeAt(0);
      let el = range.startContainer.nodeType === 3 ? range.startContainer : (range.startContainer.childNodes[range.startOffset] || range.startContainer);
      if (el && el.nodeType === 3) el = el.parentElement;
      const cs = el && el.nodeType === 1 ? window.getComputedStyle(el) : null;
      const ff = cs ? cs.fontFamily : '';
      const fs = cs ? (Math.round(parseFloat(cs.fontSize)) || DEFAULT_SIZE) : DEFAULT_SIZE;
      setBubble({ left: r.left + r.width / 2, top: r.top - 8, b, i, ff, fs });
    };
    document.addEventListener('selectionchange', onSel);
    // При прокрутке меню не прячем, а переставляем к выделению: оно fixed, а смена
    // шрифта или размера сама прокручивает редактор — меню пропадало посреди работы.
    window.addEventListener('scroll', onSel, true);
    return () => { document.removeEventListener('selectionchange', onSel); window.removeEventListener('scroll', onSel, true); };
  }, []);

  // Меню выделения закрылось — закрываем и открытый в нём список
  useEffect(() => { if (!bubble) setMenu(null); }, [bubble]);

  /* Шрифт или размер для выделенного фрагмента. Сам браузер умеет это только
     устаревшими <font face> / <font size="1–7">, зато корректно режет выделение
     и снимает то же свойство с вложенных тегов. Поэтому ставим метку штатной
     командой, а потом меняем метки на <span style="…"> с настоящим значением. */
  const applyStyle = (prop, value) => {
    const area = areaRef.current;
    const sel = window.getSelection();
    if (!area || !sel || sel.isCollapsed || !sel.rangeCount || !area.contains(sel.anchorNode)) return;
    area.focus();
    try { document.execCommand('styleWithCSS', false, false); } catch { /* */ }
    const MARK = 'rich-editor-mark';
    let marked;
    if (prop === 'font-family') {
      document.execCommand('fontName', false, MARK);
      marked = [...area.querySelectorAll(`font[face="${MARK}"], [style*="${MARK}"]`)];
    } else {
      document.execCommand('fontSize', false, '7');
      marked = [...area.querySelectorAll('font[size="7"], [style*="xxx-large"]')];
    }
    if (!marked.length) return;

    const FALLBACK = { 'font-family': 'var(--font-read)', 'font-size': `${DEFAULT_SIZE}px` };
    const spans = marked.map(node => {
      let span = node;
      if (node.tagName === 'FONT') {
        span = document.createElement('span');
        while (node.firstChild) span.appendChild(node.firstChild);
        node.replaceWith(span);
      } else {
        node.style.removeProperty(prop);   // браузер повесил метку на уже стилизованный span
      }
      // Внутри фрагмента прежнее значение этого свойства больше не действует
      span.querySelectorAll('[style]').forEach(x => {
        x.style.removeProperty(prop);
        if (!x.getAttribute('style')) x.removeAttribute('style');
      });
      if (value) span.style.setProperty(prop, value);
      else {
        // «По умолчанию»: если значение приходит от внешнего span — перебиваем явно
        let p = span.parentElement, inherited = false;
        while (p && p !== area) { if (p.style && p.style.getPropertyValue(prop)) { inherited = true; break; } p = p.parentElement; }
        if (inherited) span.style.setProperty(prop, FALLBACK[prop]);
      }
      if (!span.getAttribute('style')) span.removeAttribute('style');
      return span;
    });
    // Фрагмент остаётся выделенным — можно сразу поменять и шрифт, и размер
    const r = document.createRange();
    r.setStartBefore(spans[0]);
    r.setEndAfter(spans[spans.length - 1]);
    sel.removeAllRanges();
    sel.addRange(r);
    emit();
  };

  // Список открываем вниз, а если до нижнего края экрана места не хватает — вверх.
  // Иначе нижние пункты уходили за экран и клик по ним сбрасывал выделение.
  const toggleMenu = (e, type) => {
    const r = e.currentTarget.closest('.bubble').getBoundingClientRect();
    const need = type === 'font' ? EDITOR_FONTS.length * 33 + 24 : Math.ceil(EDITOR_SIZES.length / 5) * 32 + 24;
    const below = window.innerHeight - r.bottom - 12;
    setMenuUp(below < need && r.top - 12 > below);
    setMenu(m => (m === type ? null : type));
  };

  const curFont = bubble
    ? (fontByFamily(bubble.ff) || { label: firstFamily(bubble.ff) || 'Шрифт', value: bubble.ff })
    : null;

  const bbCmd = (e, cmd) => {
    e.preventDefault();
    const a = areaRef.current; if (a) a.focus();
    if (cmd === 'link') { const url = window.prompt('Введите ссылку:', 'https://'); if (url) document.execCommand('createLink', false, url); }
    else document.execCommand(cmd);
    emit();
  };

  return (
    <div className="editor" ref={editorRef}>
      <div className="ed-bar" onMouseDown={e => { const btn = e.target.closest('.ed-btn'); if (btn) { e.preventDefault(); run(btn.dataset.cmd); } }}>
        <button type="button" className="ed-btn ed-fill" data-cmd="undo" data-tip="Отменить">{svg(<><path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/></>)}</button>
        <button type="button" className="ed-btn ed-fill" data-cmd="redo" data-tip="Повторить">{svg(<><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13"/></>)}</button>
        {onAttachFile && <button type="button" className="ed-btn" data-cmd="attach" data-tip="Прикрепить файл">{svg(<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>, 2)}</button>}
        <span className="ed-sep" />
        <button type="button" className="ed-btn" data-cmd="image" data-tip="Вставить картинку">{svg(<><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></>)}</button>
        <button type="button" className="ed-btn" data-cmd="codeblock" data-tip="Блок кода">{svg(<><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></>)}</button>
      </div>

      <div
        ref={areaRef}
        className="ed-area"
        contentEditable
        suppressContentEditableWarning
        data-ph={placeholder}
        onInput={() => { normalizeImgs(); emit(); refreshImgSel(); }}
        onBlur={emit}
        onPaste={onPaste}
        onClick={onAreaClick}
      />

      {/* Рамка + панель размера + уголок над выбранной картинкой */}
      {imgSel && (
        <>
          <div className="ed-imgsel" style={{ left: imgSel.left, top: imgSel.top, width: imgSel.w, height: imgSel.h }} />
          <div className="ed-imgbar" style={{ left: imgSel.left + 6, top: imgSel.top + 6 }}
               onMouseDown={e => e.preventDefault()}>
            <button type="button" title="Меньше" onClick={() => resizeImg('minus')}>−</button>
            <button type="button" title="Больше" onClick={() => resizeImg('plus')}>+</button>
            <button type="button" title="Во всю ширину" onClick={() => resizeImg('fit')}>⤢</button>
            <span className="ed-imgbar-sep" />
            <button type="button" title="Слева" onClick={() => alignImg('left')}>{svg(<><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="13" y2="12"/><line x1="3" y1="18" x2="17" y2="18"/></>, 2)}</button>
            <button type="button" title="По центру" onClick={() => alignImg('center')}>{svg(<><line x1="3" y1="6" x2="21" y2="6"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="5" y1="18" x2="19" y2="18"/></>, 2)}</button>
            <button type="button" title="Справа" onClick={() => alignImg('right')}>{svg(<><line x1="3" y1="6" x2="21" y2="6"/><line x1="11" y1="12" x2="21" y2="12"/><line x1="7" y1="18" x2="21" y2="18"/></>, 2)}</button>
          </div>
          <div className="ed-imgsel-handle" title="Потяните, чтобы изменить размер"
               style={{ left: imgSel.left + imgSel.w, top: imgSel.top + imgSel.h }}
               onMouseDown={onHandleDown} />
        </>
      )}

      {bubble && (
        <div className="bubble show" style={{ left: bubble.left, top: bubble.top }}
             onMouseDown={e => {
               e.preventDefault();   // клик по меню не должен снимать выделение в тексте
               const btn = e.target.closest('.bb-btn[data-cmd]');
               if (btn) bbCmd(e, btn.dataset.cmd);
             }}>
          {/* Шрифт — названия набраны своим же шрифтом */}
          <div className="bb-dd">
            <button type="button" className={`bb-btn bb-pick${menu === 'font' ? ' open' : ''}`} data-tip={menu ? undefined : 'Шрифт'}
              onMouseDown={(e) => toggleMenu(e, 'font')}>
              <span className="bb-pick-t" style={{ fontFamily: curFont.css || curFont.value || undefined }}>{curFont.label}</span>{IcDD}
            </button>
            {menu === 'font' && (
              <div className={`bb-menu fonts${menuUp ? ' up' : ''}`}>
                {EDITOR_FONTS.map(f => (
                  <button key={f.label} type="button" className={`bb-opt${f.label === curFont.label ? ' on' : ''}`}
                    style={{ fontFamily: f.css || f.value }}
                    onMouseDown={() => { applyStyle('font-family', f.value); setMenu(null); }}>{f.label}</button>
                ))}
              </div>
            )}
          </div>
          {/* Размер */}
          <div className="bb-dd">
            <button type="button" className={`bb-btn bb-pick size${menu === 'size' ? ' open' : ''}`} data-tip={menu ? undefined : 'Размер'}
              onMouseDown={(e) => toggleMenu(e, 'size')}>
              <span className="bb-pick-t">{bubble.fs}</span>{IcDD}
            </button>
            {menu === 'size' && (
              <div className={`bb-menu sizes${menuUp ? ' up' : ''}`}>
                {EDITOR_SIZES.map(sz => (
                  <button key={sz} type="button" className={`bb-opt${sz === bubble.fs ? ' on' : ''}`}
                    onMouseDown={() => { applyStyle('font-size', sz === DEFAULT_SIZE ? '' : `${sz}px`); setMenu(null); }}>{sz}</button>
                ))}
              </div>
            )}
          </div>
          <span className="bb-sep" />
          <button type="button" className={`bb-btn${bubble.b ? ' active' : ''}`} data-cmd="bold" data-tip="Жирный"><span className="b">B</span></button>
          <button type="button" className={`bb-btn${bubble.i ? ' active' : ''}`} data-cmd="italic" data-tip="Курсив"><span className="i">I</span></button>
          <button type="button" className="bb-btn" data-cmd="link" data-tip="Ссылка">{svg(<><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></>, 2)}</button>
          <span className="bb-sep" />
          <button type="button" className="bb-btn" data-cmd="superscript" data-tip="Степень (x²)">x<sup>2</sup></button>
          <button type="button" className="bb-btn" data-cmd="subscript" data-tip="Индекс (x₂)">x<sub>2</sub></button>
        </div>
      )}
    </div>
  );
}
